import type { User } from "@supabase/supabase-js";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useBrandStore } from "@/state/store/brand";
import { useSearchSpecificationStore } from "@/state/store/searchSpecification";

export type CustomJwtAttributes = {
      app_role: string;
      session_id: string;
      organization_id: string;
};

type DecodedJwtClaims = {
      app_role?: string;
      session_id?: string;
      organization_id?: string;
};

export const useSession = () => {
      const [user, setUser] = useState<(User & CustomJwtAttributes) | null>(null);
      const [isLoading, setIsLoading] = useState(true);

      const clearSessionScopedState = () => {
            useBrandStore.getState().clear();
            useSearchSpecificationStore.getState().clear();
      };

      useEffect(() => {
            // Get initial session
            const getInitialSession = async () => {
                  try {
                        const {
                              data: { session },
                        } = await supabase.auth.getSession();

                        if (session?.user) {
                              // Try to get claims, but don't let it block
                              let appRole = null;
                              let sessionId = null;
                              let organizationId = null;
                              try {
                                    const decoded = jwtDecode<DecodedJwtClaims>(session.access_token);
                                    appRole = decoded.app_role;
                                    sessionId = decoded.session_id;
                                    organizationId = decoded.organization_id;
                              } catch (error) {
                                    console.warn("Could not decode token:", error);
                              }

                              setUser({
                                    ...session.user,
                                    organization_id: organizationId || session.user.id,
                                    app_role: appRole || "",
                                    session_id: sessionId || "",
                              });
                        } else {
                              clearSessionScopedState();
                              setUser(null);
                        }
                  } catch (error) {
                        console.error("Error getting initial session:", error);
                        clearSessionScopedState();
                        setUser(null);
                  } finally {
                        setIsLoading(false);
                  }
            };

            getInitialSession();

            // Listen for auth changes
            const {
                  data: { subscription },
            } = supabase.auth.onAuthStateChange(async (_event, session) => {
                  if (session?.user) {
                        let appRole = session.user.user_metadata?.app_role;
                        let sessionId = null;
                        let organizationId = null;
                        try {
                              const decoded = jwtDecode<DecodedJwtClaims>(session.access_token);
                              appRole = decoded.app_role || appRole;
                              sessionId = decoded.session_id || sessionId;
                              organizationId = decoded.organization_id || organizationId;
                        } catch (error) {
                              console.warn("Could not decode token:", error);
                        }

                        setUser({
                              ...session.user,
                              organization_id: organizationId || session.user.id,
                              app_role: appRole,
                              session_id: sessionId || "",
                        });
                  } else {
                        clearSessionScopedState();
                        setUser(null);
                  }

                  setIsLoading(false);
            });

            return () => subscription.unsubscribe();
      }, []);

      return { sessionData: user, isLoading };
};
